<svelte:options runes={true} />

<script lang="ts">
  import { CommandId, type CommandManager } from '@commands';
  import { entitySpecForClass, type Doc } from '@doc';
  import { Orientation, Size } from '@geom';
  import { topologicalSort, Tree } from '@graph';
  import { localeManager, prefs } from '@reactive';
  import { resourcesState } from '@resources/resourcesLoaded.svelte';
  import type { MenuSelectEventDetail } from '@shoelace-style/shoelace/dist/components/menu/menu.component.js';
  import { createEntityLayoutSpec, layoutEntity, type SnorfLayout } from '@snorf';
  import { iString, type IString, type IStringVariants } from '@strings';
  import { VisualCache } from '@visual';
  import SnorfLayoutPreview from './SnorfLayoutPreview.svelte';

  let {
    doc,
    commandManager,
    selectedUnitClass = $bindable(),
    flyoutOrientation = $bindable(),
    previewSize
  }: {
    doc: Doc;
    commandManager: CommandManager;
    selectedUnitClass: string | undefined;
    flyoutOrientation?: Orientation;
    previewSize?: Size;
  } = $props();

  // Create a shared cache for all layout previews
  let sharedCache = $state(new VisualCache());

  // Reactively get sorted unit classes from the document - this ONLY updates when classes change
  let unitClassesWithLayouts = $derived.by(() => {
    const classDetails = doc.classes.getAllClassDetails();

    // Build a tree from the class hierarchy
    const tree = new Tree<unknown, { name: IString }, unknown>({
      root: 'Element',
      rootData: { name: iString('Element') }
    });

    // Add all classes to the tree
    for (const [className, details] of classDetails) {
      if (className === 'Element') continue; // Already added as root

      const parentName = details.parent ?? 'Element';
      const classAttrs = details.attrs;
      const name = classAttrs.name as IStringVariants;
      const displayName = iString(name);

      tree.addChild(className, `${className}_edge`, {
        parent: parentName,
        data: { name: displayName }
      });
    }

    // Get topological sort of all classes
    const sortedClassNames = topologicalSort(tree.graph);

    // Filter to only include non-Junctor, non-abstract subclasses of Unit
    const unitClasses = sortedClassNames.filter(className => {
      if (!doc.classes.isClassKindOf(className, 'Unit')) {
        return false;
      }
      if (doc.classes.hasClass('Junctor')) {
        if (doc.classes.isClassKindOf(className, 'Junctor')) {
          return false;
        }
      }
      if (doc.classes.isAbstractClass(className)) {
        return false;
      }
      return true;
    });

    // Only create layouts if resources are loaded
    if (!resourcesState.resourcesLoaded) {
      // Return classes without layouts when resources aren't ready
      return unitClasses.map(className => ({
        className,
        entitySpec: entitySpecForClass(doc, className),
        layout: undefined
      }));
    }

    // Create EntitySpec and SnorfLayout for each class
    const colorMode = prefs.colorMode;
    const currentLocale = localeManager.localeId;
    const padding = 2;

    return unitClasses.map(className => {
      performance.mark(`ClassPicker:layout:${className}:start`);
      const entitySpec = entitySpecForClass(doc, className);
      const snorfSpec = createEntityLayoutSpec(entitySpec, padding);
      const layout = layoutEntity(
        entitySpec,
        snorfSpec,
        colorMode,
        currentLocale,
        sharedCache,
        { mark: className }
      );
      performance.mark(`ClassPicker:layout:${className}:end`);
      performance.measure(
        `ClassPicker:layout:${className}`,
        `ClassPicker:layout:${className}:start`,
        `ClassPicker:layout:${className}:end`
      );

      return {
        className,
        entitySpec,
        layout
      };
    });
  });

  // Extract just the class names for easy access
  let unitClasses = $derived(unitClassesWithLayouts.map(item => item.className));

  // Initialize with undefined, will be set when unitClasses is available
  // let selectedClass = $state<string | undefined>(undefined);

  // Update selected class when unit classes change
  $effect(() => {
    if (unitClasses.length > 0) {
      if (!selectedUnitClass || !unitClasses.includes(selectedUnitClass)) {
        selectedUnitClass = unitClasses[0];
      }
    }
  });

  function handleSelect(event: CustomEvent<MenuSelectEventDetail>) {
    const value = event.detail.item.value;
    console.log('Selected class:', value);
    selectedUnitClass = value;
    commandManager.dispatch(CommandId.SET_SELECTED_UNITS_CLASS);
  }

  // previewSize now provided as a bindable prop

  // Helper to get the layout for a specific class
  function getLayoutForClass(className: string): SnorfLayout | undefined {
    const item = unitClassesWithLayouts.find(item => item.className === className);
    return item?.layout;
  }

  // Define checked state
  let checkedState = $derived.by(() => {
    let result: Record<string, boolean> = {};
    for (const className of unitClasses) {
      result[className] = className === selectedUnitClass;
    }
    return result;
  });
</script>

<style>
  .dropdown-selection {
    width: 100%;
  }
  .dropdown-selection :global(sl-dropdown) {
    width: 100%;
    display: block;
  }
  /* Make the trigger button span full width */
  .dropdown-selection :global(sl-button[slot='trigger']) {
    width: 100%;
    display: block;
  }
  .dropdown-selection :global(sl-button::part(base)) {
    width: 100%;
    justify-content: center; /* center trigger content horizontally */
  }
  .dropdown-selection :global(sl-button::part(label)) {
    width: 100%;
  }
  /* Ensure dropdown panel (menu) is at least trigger width */
  .dropdown-selection :global(sl-dropdown::part(panel)) {
    min-width: 100%;
    width: 100%;
  }
  /* Make menu itself stretch */
  .dropdown-selection :global(sl-menu) {
    width: 100%;
  }
  /* Style for horizontal menu layout - target the host element directly */
  .dropdown-selection :global(sl-menu.horizontal-menu) {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: wrap !important;
    padding: var(--sl-spacing-x-small) !important;
  }

  /* Style the menu items to work well horizontally */
  .dropdown-selection :global(sl-menu.horizontal-menu sl-menu-item) {
    flex-shrink: 0;
  }

  /* Hide the button caret since we want a clean appearance */
  /* .dropdown-selection :global(sl-button::part(caret)) {
    display: none !important;
  } */

  /* Hide the submenu chevron since we don't use submenus - ClassPicker only */
  .dropdown-selection :global(sl-menu-item::part(submenu-icon)) {
    display: none !important;
  }

  /* Hide the checked icon - ClassPicker only */
  .dropdown-selection :global(sl-menu-item::part(checked-icon)) {
    display: none !important;
  }
</style>

<div class="dropdown-selection">
  {#if selectedUnitClass}
    <sl-dropdown>
      <sl-button slot="trigger" caret size="small">
        <div
          style="display:flex; align-items:center; justify-content:center; padding:0.2rem; margin:0; line-height:0; width:100%;"
          class="trigger-preview"
        >
          <SnorfLayoutPreview
            layout={getLayoutForClass(selectedUnitClass)}
            mark={selectedUnitClass}
            {previewSize}
            bind:cache={sharedCache}
          />
        </div>
      </sl-button>
      <sl-menu
        class:horizontal-menu={flyoutOrientation === Orientation.HORIZONTAL}
        onsl-select={handleSelect}
        value={selectedUnitClass}
      >
        {#each unitClasses as className}
          <sl-menu-item
            checked={checkedState[className]}
            type="checkbox"
            value={className}
          >
            <div
              style="display: flex; flex-direction: row; align-items: center; gap: 0.5rem"
            >
              <SnorfLayoutPreview
                layout={getLayoutForClass(className)}
                mark={className}
                {previewSize}
                bind:cache={sharedCache}
              />
            </div>
          </sl-menu-item>
        {/each}
      </sl-menu>
    </sl-dropdown>
  {/if}
</div>
